import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/admin/FileUpload";

interface Circuit {
  id: number;
  title: string;
  location: string;
  duration: string;
  group_size: string;
  travel_dates: string;
  price: string;
  image_url: string;
  tag: string;
  description: string;
}

const initialFormState = {
  title: "",
  location: "",
  duration: "",
  group_size: "",
  travel_dates: "",
  price: "",
  image_url: "",
  tag: "",
  description: ""
};

export default function CircuitsManager() {
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentCircuit, setCurrentCircuit] = useState<Circuit | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const { toast } = useToast();

  useEffect(() => {
    fetchCircuits();
  }, []);

  const fetchCircuits = () => {
    setLoading(true);
    setLoading(true);
    fetchAPI("?type=circuits")
      .then((data: any) => {
        setCircuits(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching circuits:", err);
        setLoading(false);
        toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to load circuits."
        });
      });
  };

  const handleOpenDialog = (circuit?: Circuit) => {
    if (circuit) {
      setCurrentCircuit(circuit);
      setFormData({
        title: circuit.title,
        location: circuit.location,
        duration: circuit.duration,
        group_size: circuit.group_size,
        travel_dates: circuit.travel_dates,
        price: circuit.price,
        image_url: circuit.image_url,
        tag: circuit.tag,
        description: circuit.description
      });
    } else {
      setCurrentCircuit(null);
      setFormData(initialFormState);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const method = currentCircuit ? "PUT" : "POST";
    const endpoint = currentCircuit 
      ? `?type=circuits&id=${currentCircuit.id}`
      : "?type=circuits";

    try {
      const result = await fetchAPI(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (result.status === "success") {
        toast({
            title: "Success",
            description: currentCircuit ? "Circuit updated" : "Circuit created"
        });
        setIsDialogOpen(false);
        fetchCircuits();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
        toast({
            variant: "destructive",
            title: "Error",
            description: err.message || "Operation failed"
        });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this circuit?")) return;

    try {
      const result = await fetchAPI(`?type=circuits&id=${id}`, {
        method: "DELETE",
      });
      if (result.status === "success") {
        toast({
            title: "Deleted",
            description: "Circuit removed successfully"
        });
        fetchCircuits();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
        toast({
            variant: "destructive",
            title: "Error",
            description: err.message || "Failed to delete"
        });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-serif font-bold">Circuits</h1>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" /> Add Circuit
        </Button>
      </div>

      <div className="border rounded-lg shadow-sm bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Price</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : circuits.length === 0 ? (
               <TableRow>
                <TableCell colSpan={6} className="text-center py-8">No circuits found.</TableCell>
              </TableRow>
            ) : (
              circuits.map((circuit) => (
                <TableRow key={circuit.id}>
                  <TableCell>
                    <img src={`https://baelleaves.com/${circuit.image_url}`} alt={circuit.title} className="w-12 h-12 rounded object-cover" loading="lazy" />
                  </TableCell>
                  <TableCell className="font-medium">{circuit.title}</TableCell>
                  <TableCell>{circuit.location}</TableCell>
                  <TableCell>{circuit.duration}</TableCell>
                  <TableCell>{circuit.price}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="outline" size="icon" onClick={() => handleOpenDialog(circuit)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="destructive" size="icon" onClick={() => handleDelete(circuit.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentCircuit ? "Edit Circuit" : "Add New Circuit"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input 
                  id="title" 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tag">Tag (Theme)</Label>
                <Input 
                  id="tag" 
                  value={formData.tag} 
                  onChange={(e) => setFormData({...formData, tag: e.target.value})} 
                  placeholder="e.g. Wellness"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Input 
                  id="location" 
                  value={formData.location} 
                  onChange={(e) => setFormData({...formData, location: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">Price</Label>
                <Input 
                  id="price" 
                  value={formData.price} 
                  onChange={(e) => setFormData({...formData, price: e.target.value})} 
                  placeholder="e.g. From $2,450"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">Duration</Label>
                <Input 
                  id="duration" 
                  value={formData.duration} 
                  onChange={(e) => setFormData({...formData, duration: e.target.value})} 
                  placeholder="e.g. 5 Days"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="group_size">Group Size</Label>
                <Input 
                  id="group_size" 
                  value={formData.group_size} 
                  onChange={(e) => setFormData({...formData, group_size: e.target.value})} 
                  placeholder="e.g. 8 travelers"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="travel_dates">Travel Dates</Label>
                <Input 
                  id="travel_dates" 
                  value={formData.travel_dates} 
                  onChange={(e) => setFormData({...formData, travel_dates: e.target.value})} 
                  placeholder="e.g. Oct - Mar"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Image</Label>
              <FileUpload 
                value={formData.image_url} 
                onChange={(url) => setFormData({...formData, image_url: url})} 
                accept="image/*"
                placeholder="Upload or enter URL"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description" 
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})} 
                rows={4}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Save Circuit</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
